import React, { useContext, useState } from 'react';

import { CompareContractsOutput } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import checkCircleTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import thumbsDownTokens from '@amzn/meridian-tokens/base/icon/thumbs-down';
import thumbsUpTokens from '@amzn/meridian-tokens/base/icon/thumbs-up';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { AppContext } from 'src/contexts/appContext';

interface SummaryFooterProps {
    feedbackState: 'neutral' | 'thumbs-up' | 'thumbs-down';
    onFeedbackChange: (state: 'neutral' | 'thumbs-up' | 'thumbs-down') => void;
    summaryData: CompareContractsOutput;
    contractContext: {
        leftId?: number;
        rightId?: number;
        leftTimestamp?: string;
        rightTimestamp?: string;
        contractIdMap?: Map<string, string>;
    };
}

function generateMarkdownContent(
    summaryData: CompareContractsOutput,
    contractContext: {
        leftId?: number;
        rightId?: number;
        leftTimestamp?: string;
        rightTimestamp?: string;
        contractIdMap?: Map<string, string>;
    }
): string {
    const { leftId, rightId, leftTimestamp, rightTimestamp, contractIdMap } = contractContext;

    // Handle edge cases for contract information
    const leftName = leftId ? (contractIdMap?.get(`${leftId}`) ?? `Contract ${leftId}`) : 'Unknown Contract';
    const rightName = rightId ? (contractIdMap?.get(`${rightId}`) ?? `Contract ${rightId}`) : 'Unknown Contract';

    const header = `# Contract Comparison Summary

## Contracts Compared
- **Left Contract**: ${leftName}${leftTimestamp ? ` (${leftTimestamp})` : ''}
- **Right Contract**: ${rightName}${rightTimestamp ? ` (${rightTimestamp})` : ''}

## Summary`;

    // Handle edge cases for summary points
    const summaryPoints = summaryData?.summary?.summaryPoints;
    let bullets = '';

    if (summaryPoints && summaryPoints.length > 0) {
        bullets = summaryPoints
            .filter((point) => point && point.trim().length > 0) // Filter out empty points
            .map((point) => `- ${point.trim()}`)
            .join('\n');
    } else {
        bullets = '- No significant differences found between contracts.';
    }

    // Handle edge cases for metadata
    const metadata = summaryData?.summary?.metadata;
    const generatedDate = metadata?.generatedAt
        ? new Date(metadata.generatedAt).toLocaleString()
        : new Date().toLocaleString();
    const modelUsed = metadata?.modelUsed ?? 'Unknown AI Model';
    const processingTime = metadata?.processingTimeMs ? `${metadata.processingTimeMs}ms` : 'Unknown';

    const footer = `

## Generation Details
- **Generated**: ${generatedDate}
- **Model**: ${modelUsed}
- **Processing Time**: ${processingTime}

---
*Response was generated by AI, please review and validate for accuracy*`;

    return `${header}\n\n${bullets}${footer}`;
}

export const SummaryFooter: React.FC<SummaryFooterProps> = ({
    feedbackState,
    onFeedbackChange,
    summaryData,
    contractContext
}) => {
    const { setToasts } = useContext(AppContext);
    const [copyIconState, setCopyIconState] = useState<'default' | 'success'>('default');

    const handleThumbsUp = () => {
        onFeedbackChange(feedbackState === 'thumbs-up' ? 'neutral' : 'thumbs-up');
    };

    const handleThumbsDown = () => {
        onFeedbackChange(feedbackState === 'thumbs-down' ? 'neutral' : 'thumbs-down');
    };

    const handleCopy = async () => {
        try {
            const markdownContent = generateMarkdownContent(summaryData, contractContext);

            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(markdownContent);
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = markdownContent;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);

                if (!successful) {
                    throw new Error('Fallback copy method failed');
                }
            }

            // Visual feedback: temporary icon change
            setCopyIconState('success');
            setTimeout(() => setCopyIconState('default'), 2000);

            // Toast notification with auto-dismiss
            const toastId = `summary-copy-success-${Date.now()}`;
            setToasts((prev) => [
                ...prev,
                {
                    id: toastId,
                    type: 'success',
                    message: 'Summary copied to clipboard!'
                }
            ]);

            // Auto-dismiss toast after 3 seconds
            setTimeout(() => {
                setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
            }, 3000);
        } catch (error) {
            console.error('Copy to clipboard failed:', error);

            // Provide specific error messages based on the error type
            let errorMessage = 'Failed to copy summary to clipboard';
            if (error instanceof Error) {
                if (error.name === 'NotAllowedError') {
                    errorMessage = 'Clipboard access denied. Please check your browser permissions.';
                } else if (error.name === 'NotSupportedError') {
                    errorMessage = 'Clipboard functionality is not supported in this browser.';
                }
            }

            // Error toast with auto-dismiss
            const toastId = `summary-copy-error-${Date.now()}`;
            setToasts((prev) => [
                ...prev,
                {
                    id: toastId,
                    type: 'error',
                    message: errorMessage
                }
            ]);

            // Auto-dismiss error toast after 5 seconds
            setTimeout(() => {
                setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
            }, 5000);
        }
    };

    return (
        <Row spacing="m" alignmentHorizontal="justify" alignmentVertical="center">
            <Text type="b200" color="secondary">
                <em>This response was generated by AI. Please review and validate for accuracy</em>
            </Text>

            <Row spacing="s" alignmentVertical="center">
                <Text type="b200" color="secondary">
                    <em>Help improve this experience</em>
                </Text>
                <Button
                    type={feedbackState === 'thumbs-up' ? 'primary' : 'icon'}
                    onClick={handleThumbsUp}
                    aria-label="Mark summary as helpful"
                >
                    <Icon tokens={thumbsUpTokens} />
                </Button>
                <Button
                    type={feedbackState === 'thumbs-down' ? 'primary' : 'icon'}
                    onClick={handleThumbsDown}
                    aria-label="Mark summary as not helpful"
                >
                    <Icon tokens={thumbsDownTokens} />
                </Button>
                <Text type="b200" color="secondary">
                    |
                </Text>
                <Button type="icon" onClick={handleCopy} aria-label="Copy summary to clipboard">
                    <Icon tokens={copyIconState === 'success' ? checkCircleTokens : copyTokens} />
                </Button>
            </Row>
        </Row>
    );
};
